import { useMemo } from "react";

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

// Buckets a member row (or follow-up candidate) into a scheduling window
// based on its `nextActionAt` relative to `now`.
export function bucketByDueDate(dueAt, now = new Date()) {
  if (!dueAt) return "later";
  const target = new Date(dueAt);
  if (Number.isNaN(target.getTime())) return "later";

  const dayStart = startOfDay(now);
  const dayEnd = endOfDay(now);
  const tomorrowEnd = new Date(dayEnd);
  tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);
  const weekEnd = new Date(dayEnd);
  weekEnd.setDate(weekEnd.getDate() + 7);

  if (target < dayStart) return "overdue";
  if (target <= dayEnd) return "today";
  if (target <= tomorrowEnd) return "tomorrow";
  if (target <= weekEnd) return "this_week";
  return "later";
}

export const BUCKET_ORDER = ["overdue", "today", "tomorrow", "this_week", "later"];

export const BUCKET_LABELS = {
  overdue: "Overdue",
  today: "Today",
  tomorrow: "Tomorrow",
  this_week: "This week",
  later: "Later",
};

// Builds a synthesized "reason" chip for why a follow-up is due, using data
// already on the row: current step, sequence length, last activity recency.
export function describeFollowUpReason(row, now = new Date()) {
  const cl = row?.campaignLead || {};
  const stepIndex = typeof cl.stepIndex === "number" ? cl.stepIndex : null;
  const lastTouch = row?.lastActivity?.occurredAt || cl.lastSentAt || cl.updatedAt;
  let ago = null;
  if (lastTouch) {
    const diffMs = now.getTime() - new Date(lastTouch).getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days >= 1) ago = `${days}d since last touch`;
    else if (diffMs > 0) ago = "today";
  }

  if (cl.stage === "replied") {
    return ago ? `Replied · ${ago}` : "Reply needs a response";
  }
  if (stepIndex == null) {
    return ago ? `Needs outreach · ${ago}` : "Needs outreach";
  }
  const stepLabel = `Step ${stepIndex + 1}`;
  if (!ago) return stepLabel;
  return `${stepLabel} · ${ago}`;
}

// Builds the rows for each smart view from the existing workspace data
// without any new API calls.
export default function useSmartView({
  smartView,
  memberRows,
  queueRows,
  activityRows,
}) {
  return useMemo(() => {
    const now = new Date();

    if (smartView === "queue") {
      const grouped = { overdue: [], today: [], tomorrow: [], this_week: [], later: [] };
      for (const row of queueRows || []) {
        const bucket = bucketByDueDate(row.queueItem?.dueAt, now);
        grouped[bucket].push(row);
      }
      return { kind: "queue", grouped, total: queueRows?.length || 0 };
    }

    if (smartView === "replies") {
      const replies = (activityRows || []).filter(
        (row) => row.activity?.direction === "inbound"
      );
      return { kind: "replies", rows: replies, total: replies.length };
    }

    if (smartView === "followups") {
      const candidates = (memberRows || []).filter((row) => {
        const cl = row.campaignLead;
        if (!cl) return false;
        if (cl.enrollmentStatus && cl.enrollmentStatus !== "active") return false;
        return Boolean(cl.nextActionAt);
      });
      const grouped = { overdue: [], today: [], tomorrow: [], this_week: [], later: [] };
      for (const row of candidates) {
        const bucket = bucketByDueDate(row.campaignLead.nextActionAt, now);
        grouped[bucket].push(row);
      }
      return { kind: "followups", grouped, total: candidates.length };
    }

    if (smartView === "today") {
      const dueLeads = (memberRows || []).filter((row) => {
        const next = row.campaignLead?.nextActionAt;
        if (!next) return false;
        const bucket = bucketByDueDate(next, now);
        return bucket === "overdue" || bucket === "today";
      });
      const dueQueue = (queueRows || []).filter((row) => {
        const bucket = bucketByDueDate(row.queueItem?.dueAt, now);
        return bucket === "overdue" || bucket === "today";
      });
      const replies = (activityRows || []).filter(
        (row) => row.activity?.direction === "inbound"
      );
      return {
        kind: "today",
        followUps: dueLeads,
        queue: dueQueue,
        replies,
        total: dueLeads.length + dueQueue.length + replies.length,
      };
    }

    return { kind: "all", rows: memberRows || [], total: memberRows?.length || 0 };
  }, [smartView, memberRows, queueRows, activityRows]);
}
